// Recurrence (rrule) helpers for the unified working-hours flow.
//
// We keep the same wire format the rest of the app already uses (the doctor
// working-hour rules built by DoctorHour.vue and matched by checkRruleIfSameDay):
// every rule is FREQ=MONTHLY with a DTSTART, BYDAY for the weekday(s), and an
// optional BYSETPOS for the "Nth weekday of the month" case. "Weekly" is simply
// a monthly rule with no BYSETPOS (every matching weekday each month). An
// optional UNTIL bounds the series (used when splitting on "this & following").
import { datetime, RRule } from "rrule";

// Calendar/UI weekday order (Mon-first) with Thai labels.
export const WEEKDAYS = [
  { code: "MO", label: "จ" },
  { code: "TU", label: "อ" },
  { code: "WE", label: "พ" },
  { code: "TH", label: "พฤ" },
  { code: "FR", label: "ศ" },
  { code: "SA", label: "ส" },
  { code: "SU", label: "อา" },
];

// JS Date.getDay(): 0 = Sunday .. 6 = Saturday.
const JS_DAY_TO_CODE = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

// Convert "YYYY-MM-DD" to the rrule UTC datetime the rest of the app uses.
const ymdToDatetime = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return datetime(y, m, d);
};

// The rrule weekday code for a "YYYY-MM-DD" date (e.g. "MO").
export const weekdayCodeOf = (dateStr) =>
  JS_DAY_TO_CODE[new Date(`${dateStr}T00:00:00`).getDay()];

/**
 * Build an rrule string from form state.
 * @param {Object} p
 * @param {string[]} p.weekdays  e.g. ["MO","WE"]
 * @param {number[]} [p.setpos]  e.g. [1,3] for 1st & 3rd weekday; empty = weekly
 * @param {string}   p.dtstart   "YYYY-MM-DD" the series starts on
 * @param {string|null} [p.until] "YYYY-MM-DD" inclusive end, or null for open-ended
 * @returns {string} rrule string (DTSTART + RRULE lines)
 */
export const buildRruleStr = ({ weekdays, setpos = [], dtstart, until = null }) => {
  const opts = {
    freq: RRule.MONTHLY,
    byweekday: weekdays.map((c) => RRule[c]),
    dtstart: ymdToDatetime(dtstart),
  };
  if (setpos && setpos.length) opts.bysetpos = setpos;
  if (until) opts.until = ymdToDatetime(until);
  return new RRule(opts).toString();
};

const captured = (rruleStr, re) => {
  const m = (rruleStr || "").match(re);
  return m && m[1] ? m[1] : "";
};

/**
 * Parse an rrule string back into form state for editing.
 * @returns {{weekdays:string[], setpos:number[], until:string|null, frequency:'weekly'|'monthly'}}
 */
export const parseRruleStr = (rruleStr) => {
  const weekdays = captured(rruleStr, /BYDAY=([A-Z,]+)/)
    .split(",")
    .filter(Boolean);
  const setpos = captured(rruleStr, /BYSETPOS=([-0-9,]+)/)
    .split(",")
    .filter(Boolean) // guard: "".split(",") -> [""] and Number("") is 0, not NaN
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  const toIso = (raw) =>
    raw ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}` : null;
  const until = toIso(captured(rruleStr, /UNTIL=([0-9T]+Z?)/)); // 20260614T..Z
  const dtstart = toIso(captured(rruleStr, /DTSTART[^:]*:([0-9T]+Z?)/));
  return {
    weekdays,
    setpos,
    until,
    dtstart,
    frequency: setpos.length ? "monthly" : "weekly",
  };
};
